/**
 * customerAddress — where a customer's address actually comes from. PURE.
 *
 * A customer record is rarely the whole story: older customers have no `addresses`
 * array and their street only exists on the last ORDER they placed. The profile has
 * always resolved that chain, but the Add Order modal read `customer.addresses[0]`
 * directly — so a customer whose address showed fine on screen got an order created
 * with a BLANK address. The resolution lives here now so every screen agrees.
 */

const hasStreet = (a) => !!(a && (a.fullAddress || a.address));

const NO_STREET = 'No street address provided';

/**
 * @param {object} customer    the customer record
 * @param {object} orderAddress address lifted from the customer's latest order, if any
 * @returns {Array<{fullAddress, city, state, pincode}>} best-known addresses, primary first
 */
export function resolveAddresses(customer, orderAddress) {
    const c = customer || {};

    // 1. Saved addresses that actually carry a street.
    const saved = Array.isArray(c.addresses) ? c.addresses : [];
    if (saved.some(hasStreet)) return saved;

    // 2. Flat customer.address.
    if (c.address) {
        return [{ fullAddress: c.address, city: c.city || '', state: c.state || '', pincode: c.pincode || '' }];
    }

    // 3. The latest order's address — the only place an imported customer's street lives.
    if (orderAddress && (orderAddress.fullAddress || orderAddress.pincode)) {
        return [{
            fullAddress: orderAddress.fullAddress || NO_STREET,
            city: orderAddress.city || c.city || '',
            state: orderAddress.state || c.state || '',
            pincode: orderAddress.pincode || c.pincode || '',
        }];
    }

    // 4. City/state/pincode only — enough to quote shipping, not to deliver.
    if (c.city || c.state || c.pincode) {
        return [{ fullAddress: NO_STREET, city: c.city || '', state: c.state || '', pincode: c.pincode || '' }];
    }

    return [];
}

/** The address an order should ship to. Never invents a street. */
export function primaryAddress(customer, orderAddress) {
    const a = resolveAddresses(customer, orderAddress)[0] || {};
    const street = a.fullAddress === NO_STREET ? '' : (a.fullAddress || a.address || '');
    return {
        fullAddress: street,
        city: a.city || customer?.city || '',
        state: a.state || customer?.state || '',
        pincode: a.pincode || customer?.pincode || '',
    };
}

/** One-line form for showing the operator what the order will ship to. */
export function addressLine(addr) {
    const parts = [addr?.fullAddress, addr?.city, addr?.state, addr?.pincode].map(x => String(x || '').trim()).filter(Boolean);
    return parts.join(', ');
}

export { NO_STREET };
